import React, { useState, useEffect } from 'react';
import axios from 'axios';
import DashboardLayout from '../../components/DashboardLayout';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { Search, Plus, Edit, Trash2, BookOpen, Loader2, Mail, Phone } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Teachers = () => {
    const [teachers, setTeachers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingTeacher, setEditingTeacher] = useState(null);
    const [saving, setSaving] = useState(false);
    const [formData, setFormData] = useState({
        first_name: '',
        last_name: '',
        arabic_name: '',
        date_of_birth: '',
        ppsn: '',
        address: '',
        mobile: '',
        personal_email: '',
        work_email: '',
        bank_iban: '',
        hourly_rate: 0
    });

    useEffect(() => {
        fetchTeachers();
    }, []);

    const fetchTeachers = async () => {
        try {
            const response = await axios.get(`${API_URL}/api/teachers`, { withCredentials: true });
            setTeachers(response.data);
        } catch (error) {
            toast.error('Failed to load teachers');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            if (editingTeacher) {
                await axios.put(`${API_URL}/api/teachers/${editingTeacher.id}`, formData, { withCredentials: true });
                toast.success('Teacher updated');
            } else {
                await axios.post(`${API_URL}/api/teachers`, formData, { withCredentials: true });
                toast.success('Teacher created');
            }
            setDialogOpen(false);
            resetForm();
            fetchTeachers();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to save teacher');
        } finally {
            setSaving(false);
        }
    };

    const handleEdit = (teacher) => {
        setEditingTeacher(teacher);
        setFormData({
            first_name: teacher.first_name,
            last_name: teacher.last_name,
            arabic_name: teacher.arabic_name || '',
            date_of_birth: teacher.date_of_birth || '',
            ppsn: teacher.ppsn || '',
            address: teacher.address || '',
            mobile: teacher.mobile || '',
            personal_email: teacher.personal_email || '',
            work_email: teacher.work_email || '',
            bank_iban: teacher.bank_iban || '',
            hourly_rate: teacher.hourly_rate || 0
        });
        setDialogOpen(true);
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Are you sure you want to delete this teacher?')) return;
        try {
            await axios.delete(`${API_URL}/api/teachers/${id}`, { withCredentials: true });
            toast.success('Teacher deleted');
            fetchTeachers();
        } catch (error) {
            toast.error('Failed to delete teacher');
        }
    };

    const resetForm = () => {
        setEditingTeacher(null);
        setFormData({
            first_name: '', last_name: '', arabic_name: '', date_of_birth: '', ppsn: '',
            address: '', mobile: '', personal_email: '', work_email: '', bank_iban: '', hourly_rate: 0
        });
    };

    const filteredTeachers = teachers.filter(t =>
        t.first_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        t.last_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        t.work_email?.toLowerCase().includes(searchQuery.toLowerCase())
    );

    if (loading) {
        return (
            <DashboardLayout>
                <div className="flex items-center justify-center h-64">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#064E3B]"></div>
                </div>
            </DashboardLayout>
        );
    }

    return (
        <DashboardLayout>
            <div className="space-y-6" data-testid="teachers-page">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div>
                        <h1 className="text-2xl font-bold text-[#1F2937] font-['Outfit']">Teachers</h1>
                        <p className="text-[#4B5563]">Manage school teachers</p>
                    </div>
                    <Button onClick={() => { resetForm(); setDialogOpen(true); }} className="bg-[#064E3B] hover:bg-[#047857]" data-testid="add-teacher-btn">
                        <Plus className="h-4 w-4 mr-2" /> Add Teacher
                    </Button>
                </div>

                <Card className="card-surface">
                    <CardContent className="p-4">
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#4B5563]" />
                            <Input placeholder="Search teachers..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="pl-9" />
                        </div>
                    </CardContent>
                </Card>

                <Card className="card-surface">
                    <CardContent className="p-0">
                        {filteredTeachers.length > 0 ? (
                            <div className="overflow-x-auto">
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead>Teacher</TableHead>
                                            <TableHead>Contact</TableHead>
                                            <TableHead>Hourly Rate</TableHead>
                                            <TableHead className="text-right">Actions</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {filteredTeachers.map((teacher) => (
                                            <TableRow key={teacher.id}>
                                                <TableCell>
                                                    <div className="flex items-center space-x-3">
                                                        <div className="h-10 w-10 rounded-full bg-[#064E3B] flex items-center justify-center text-white font-medium">
                                                            {teacher.first_name?.[0]}{teacher.last_name?.[0]}
                                                        </div>
                                                        <div>
                                                            <p className="font-medium">{teacher.first_name} {teacher.last_name}</p>
                                                            <p className="text-xs text-[#4B5563]">{teacher.arabic_name}</p>
                                                        </div>
                                                    </div>
                                                </TableCell>
                                                <TableCell>
                                                    <div className="space-y-1">
                                                        <div className="flex items-center text-sm">
                                                            <Mail className="h-3 w-3 mr-1 text-[#4B5563]" />
                                                            {teacher.work_email}
                                                        </div>
                                                        <div className="flex items-center text-sm text-[#4B5563]">
                                                            <Phone className="h-3 w-3 mr-1" />
                                                            {teacher.mobile}
                                                        </div>
                                                    </div>
                                                </TableCell>
                                                <TableCell>
                                                    <Badge className="bg-[#10B981] tabular-nums">€{teacher.hourly_rate?.toFixed(2)}/hr</Badge>
                                                </TableCell>
                                                <TableCell className="text-right">
                                                    <div className="flex justify-end space-x-2">
                                                        <Button size="sm" variant="outline" onClick={() => handleEdit(teacher)}>
                                                            <Edit className="h-4 w-4" />
                                                        </Button>
                                                        <Button size="sm" variant="outline" className="text-[#EF4444]" onClick={() => handleDelete(teacher.id)}>
                                                            <Trash2 className="h-4 w-4" />
                                                        </Button>
                                                    </div>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </div>
                        ) : (
                            <div className="flex flex-col items-center justify-center py-12">
                                <BookOpen className="h-12 w-12 text-[#4B5563]/30 mb-4" />
                                <p className="text-[#4B5563] mb-4">No teachers found</p>
                                <Button onClick={() => { resetForm(); setDialogOpen(true); }} className="bg-[#064E3B] hover:bg-[#047857]">
                                    <Plus className="h-4 w-4 mr-2" /> Add First Teacher
                                </Button>
                            </div>
                        )}
                    </CardContent>
                </Card>

                <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                    <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                        <DialogHeader>
                            <DialogTitle className="font-['Outfit']">{editingTeacher ? 'Edit Teacher' : 'Add New Teacher'}</DialogTitle>
                        </DialogHeader>
                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label>First Name</Label>
                                    <Input value={formData.first_name} onChange={(e) => setFormData({...formData, first_name: e.target.value})} required />
                                </div>
                                <div className="space-y-2">
                                    <Label>Last Name</Label>
                                    <Input value={formData.last_name} onChange={(e) => setFormData({...formData, last_name: e.target.value})} required />
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label>Arabic Name</Label>
                                    <Input value={formData.arabic_name} onChange={(e) => setFormData({...formData, arabic_name: e.target.value})} required />
                                </div>
                                <div className="space-y-2">
                                    <Label>Date of Birth</Label>
                                    <Input type="date" value={formData.date_of_birth} onChange={(e) => setFormData({...formData, date_of_birth: e.target.value})} required />
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label>PPSN</Label>
                                    <Input value={formData.ppsn} onChange={(e) => setFormData({...formData, ppsn: e.target.value})} required />
                                </div>
                                <div className="space-y-2">
                                    <Label>Mobile</Label>
                                    <Input value={formData.mobile} onChange={(e) => setFormData({...formData, mobile: e.target.value})} required />
                                </div>
                            </div>
                            <div className="space-y-2">
                                <Label>Address</Label>
                                <Input value={formData.address} onChange={(e) => setFormData({...formData, address: e.target.value})} required />
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label>Personal Email</Label>
                                    <Input type="email" value={formData.personal_email} onChange={(e) => setFormData({...formData, personal_email: e.target.value})} required />
                                </div>
                                <div className="space-y-2">
                                    <Label>Work Email</Label>
                                    <Input type="email" value={formData.work_email} onChange={(e) => setFormData({...formData, work_email: e.target.value})} required />
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label>Bank IBAN</Label>
                                    <Input value={formData.bank_iban} onChange={(e) => setFormData({...formData, bank_iban: e.target.value})} required />
                                </div>
                                <div className="space-y-2">
                                    <Label>Hourly Rate (€)</Label>
                                    <Input type="number" step="0.01" value={formData.hourly_rate} onChange={(e) => setFormData({...formData, hourly_rate: parseFloat(e.target.value) || 0})} required />
                                </div>
                            </div>
                            <DialogFooter>
                                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
                                <Button type="submit" disabled={saving} className="bg-[#064E3B] hover:bg-[#047857]">
                                    {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                                    {editingTeacher ? 'Update' : 'Create'}
                                </Button>
                            </DialogFooter>
                        </form>
                    </DialogContent>
                </Dialog>
            </div>
        </DashboardLayout>
    );
};

export default Teachers;
